import { useState, useEffect } from 'react';
import { api } from '../services/api';

function AgentManagement() {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showHaloImport, setShowHaloImport] = useState(false);
  const [haloAgents, setHaloAgents] = useState([]);
  const [importingAgents, setImportingAgents] = useState(new Set());
  const [bookingLinks, setBookingLinks] = useState({}); // agentId -> {url, linkId}
  const [copiedAgentId, setCopiedAgentId] = useState(null);
  const [regeneratingAgentId, setRegeneratingAgentId] = useState(null);
  const [updatingBufferAgentId, setUpdatingBufferAgentId] = useState(null);
  const [updatingHorizonAgentId, setUpdatingHorizonAgentId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    title: 'Support Agent',
    timezone: 'America/New_York',
  });

  useEffect(() => {
    loadAgents();
  }, []);

  // Load booking links for all agents
  const loadBookingLinks = async (agentList) => {
    const links = {};
    await Promise.all(
      agentList.map(async (agent) => {
        try {
          const linkData = await api.getAgentBookingLink(agent.agentId);
          links[agent.agentId] = linkData;
        } catch (error) {
          console.error(`Error loading booking link for agent ${agent.agentId}:`, error);
        }
      })
    );
    setBookingLinks(links);
  };

  const loadAgents = async () => {
    try {
      const data = await api.getAgents(false); // Get all agents including inactive
      setAgents(data);
      setLoading(false);
      // Load booking links after agents are loaded
      loadBookingLinks(data);
    } catch (error) {
      console.error('Error loading agents:', error);
      setLoading(false);
    }
  };

  const loadHaloAgents = async () => {
    try {
      setLoading(true);
      const data = await api.getHaloAgents();
      setHaloAgents(data);
      setShowHaloImport(true);
      setLoading(false);
    } catch (error) {
      console.error('Error loading Halo agents:', error);
      alert('Failed to load Halo agents. Check Halo PSA connection.');
      setLoading(false);
    }
  };

  const handleImportAgent = async (haloAgent) => {
    try {
      setImportingAgents(prev => new Set(prev).add(haloAgent.haloAgentId));
      await api.importHaloAgent(haloAgent.haloAgentId);

      // Refresh both lists
      await loadAgents();
      await loadHaloAgents();

      setImportingAgents(prev => {
        const next = new Set(prev);
        next.delete(haloAgent.haloAgentId);
        return next;
      });
    } catch (error) {
      console.error('Error importing agent:', error);
      alert('Failed to import agent');
      setImportingAgents(prev => {
        const next = new Set(prev);
        next.delete(haloAgent.haloAgentId);
        return next;
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.createAgent(formData);
      setFormData({
        name: '',
        email: '',
        title: 'Support Agent',
        timezone: 'America/New_York',
      });
      setShowForm(false);
      loadAgents();
    } catch (error) {
      console.error('Error creating agent:', error);
      alert('Failed to create agent');
    }
  };

  const toggleAgentStatus = async (agent) => {
    try {
      await api.updateAgent(agent.agentId, {
        isActive: !agent.isActive,
      });
      loadAgents();
    } catch (error) {
      console.error('Error updating agent:', error);
      alert('Failed to update agent');
    }
  };

  const copyBookingLink = async (agentId) => {
    const link = bookingLinks[agentId];
    if (link?.url) {
      try {
        await navigator.clipboard.writeText(link.url);
        setCopiedAgentId(agentId);
        setTimeout(() => setCopiedAgentId(null), 2000);
      } catch (error) {
        console.error('Error copying link:', error);
        alert('Failed to copy link');
      }
    }
  };

  const regenerateBookingLink = async (agent) => {
    if (!confirm(`Regenerate booking link for ${agent.name}? The old link will stop working.`)) {
      return;
    }
    try {
      setRegeneratingAgentId(agent.agentId);
      const linkData = await api.regenerateAgentBookingLink(agent.agentId);
      setBookingLinks(prev => ({ ...prev, [agent.agentId]: linkData }));
      setRegeneratingAgentId(null);
    } catch (error) {
      console.error('Error regenerating booking link:', error);
      alert('Failed to regenerate booking link');
      setRegeneratingAgentId(null);
    }
  };

  const updateBufferTime = async (agent, bufferValue) => {
    try {
      setUpdatingBufferAgentId(agent.agentId);
      const bufferTime = bufferValue === '' ? null : parseInt(bufferValue, 10);
      await api.updateAgent(agent.agentId, { bufferTime });
      setAgents(prev =>
        prev.map(a => a.agentId === agent.agentId ? { ...a, bufferTime } : a)
      );
      setUpdatingBufferAgentId(null);
    } catch (error) {
      console.error('Error updating buffer time:', error);
      alert('Failed to update buffer time');
      setUpdatingBufferAgentId(null);
    }
  };

  const updateMaxBookingDays = async (agent, value) => {
    try {
      setUpdatingHorizonAgentId(agent.agentId);
      const maxBookingDays = value === '' ? null : parseInt(value, 10);
      await api.updateAgent(agent.agentId, { maxBookingDays });
      setAgents(prev =>
        prev.map(a => a.agentId === agent.agentId ? { ...a, maxBookingDays } : a)
      );
      setUpdatingHorizonAgentId(null);
    } catch (error) {
      console.error('Error updating booking horizon:', error);
      alert('Failed to update booking horizon');
      setUpdatingHorizonAgentId(null);
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading agents...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Agent Management</h2>
        <div className="flex gap-3">
          <button
            onClick={() => showHaloImport ? setShowHaloImport(false) : loadHaloAgents()}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors"
          >
            {showHaloImport ? 'Hide Halo Agents' : '📥 Import from Halo'}
          </button>
          <button
            onClick={() => setShowForm(!showForm)}
            className="bg-primary-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors"
          >
            {showForm ? 'Cancel' : '+ Add Agent Manually'}
          </button>
        </div>
      </div>

      {/* Halo Import Panel */}
      {showHaloImport && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Import Agents from Halo PSA
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Select agents to import. Their details will be synced from Halo PSA.
          </p>
          <div className="space-y-3">
            {haloAgents.map((haloAgent) => (
              <div
                key={haloAgent.haloAgentId}
                className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between"
              >
                <div>
                  <div className="font-medium text-gray-900">{haloAgent.name}</div>
                  <div className="text-sm text-gray-500">{haloAgent.email}</div>
                  <div className="text-sm text-gray-500">{haloAgent.title}</div>
                </div>
                <div>
                  {haloAgent.isImported ? (
                    <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-medium rounded">
                      ✓ Imported
                    </span>
                  ) : (
                    <button
                      onClick={() => handleImportAgent(haloAgent)}
                      disabled={importingAgents.has(haloAgent.haloAgentId)}
                      className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
                    >
                      {importingAgents.has(haloAgent.haloAgentId) ? 'Importing...' : 'Import'}
                    </button>
                  )}
                </div>
              </div>
            ))}
            {haloAgents.length === 0 && (
              <div className="text-center py-6 text-gray-500">
                No Halo agents found
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add Agent Form */}
      {showForm && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Agent</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                  placeholder="Support Agent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Timezone
                </label>
                <select
                  value={formData.timezone}
                  onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                >
                  <option value="America/New_York">Eastern Time</option>
                  <option value="America/Chicago">Central Time</option>
                  <option value="America/Denver">Mountain Time</option>
                  <option value="America/Los_Angeles">Pacific Time</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              className="bg-primary-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors"
            >
              Create Agent
            </button>
          </form>
        </div>
      )}

      {/* Agents List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Agent
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Title
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Source
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Buffer Time
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Booking Horizon
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Booking Link
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {agents.map((agent) => (
              <tr key={agent.agentId}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    <div className="h-10 w-10 flex-shrink-0 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-primary-600 font-semibold">
                        {agent.name.charAt(0)}
                      </span>
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-gray-900">{agent.name}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {agent.email}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {agent.title}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {agent.haloAgentId ? (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-primary-100 text-primary-800">
                      Halo PSA
                    </span>
                  ) : (
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">
                      Manual
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      agent.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {agent.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <select
                    value={agent.bufferTime || ''}
                    onChange={(e) => updateBufferTime(agent, e.target.value)}
                    disabled={updatingBufferAgentId === agent.agentId}
                    className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                  >
                    <option value="">None</option>
                    <option value="5">5 min</option>
                    <option value="10">10 min</option>
                    <option value="15">15 min</option>
                    <option value="30">30 min</option>
                    <option value="45">45 min</option>
                    <option value="60">60 min</option>
                  </select>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <select
                    value={agent.maxBookingDays || ''}
                    onChange={(e) => updateMaxBookingDays(agent, e.target.value)}
                    disabled={updatingHorizonAgentId === agent.agentId}
                    className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                  >
                    <option value="">None</option>
                    <option value="7">7 days</option>
                    <option value="10">10 days</option>
                    <option value="14">14 days</option>
                    <option value="21">21 days</option>
                    <option value="30">30 days</option>
                    <option value="60">60 days</option>
                    <option value="90">90 days</option>
                  </select>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  {bookingLinks[agent.agentId] ? (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => copyBookingLink(agent.agentId)}
                        className="text-primary-600 hover:text-primary-900 font-medium"
                        title={bookingLinks[agent.agentId].url}
                      >
                        {copiedAgentId === agent.agentId ? 'Copied!' : 'Copy Link'}
                      </button>
                      <button
                        onClick={() => regenerateBookingLink(agent)}
                        disabled={regeneratingAgentId === agent.agentId}
                        className="text-gray-400 hover:text-gray-600 text-xs"
                        title="Regenerate link (invalidates old link)"
                      >
                        {regeneratingAgentId === agent.agentId ? '...' : 'Regenerate'}
                      </button>
                    </div>
                  ) : (
                    <span className="text-gray-400">Loading...</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <button
                    onClick={() => toggleAgentStatus(agent)}
                    className={`${
                      agent.isActive ? 'text-red-600 hover:text-red-900' : 'text-green-600 hover:text-green-900'
                    }`}
                  >
                    {agent.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {agents.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No agents found. Import from Halo or add manually.
          </div>
        )}
      </div>
    </div>
  );
}

export default AgentManagement;
